import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import { ModalMode } from '@lib/components/Modal/enums';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import {
  GetUrlParams,
  ORDERING_DIRECTION_DESC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import useAiUsageByUserQueryHook from 'utils/fetch/hooks/useAiUsageByUserQueryHook';
import getAiUsageTableColumns from '../utils/getAiUsageTableColumns';
import toAiUsageRows from '../utils/toAiUsageRows';

// The shared ORDERING_PARAMS default to `createdAt`, which this query cannot
// sort by — rank by spend instead, matching the Top-5 summary behind the modal.
const DEFAULT_ORDERING: OrderingParams = {
  [GetUrlParams.OrderingField]: 'creditsUsed',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_DESC,
};

function AiUsageByUser() {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  // Kept in local state rather than the URL: the modal sits on the Company plan
  // settings page and must not rewrite its query string.
  const paginationState = useState<PaginationParams>(PAGINATION_PARAMS);
  const orderingState = useState<OrderingParams>(DEFAULT_ORDERING);
  const [paginationParams, setPaginationParams] = paginationState;
  const [orderingParams] = orderingState;

  const { collection, loading, totalCount } = useAiUsageByUserQueryHook({
    orderingParams,
    paginationParams,
    searchQuery,
  });

  const rows = useMemo(() => toAiUsageRows(collection), [collection]);
  const columns = useMemo(
    () => getAiUsageTableColumns({ language: i18n.language, t }),
    [i18n.language, t],
  );

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      // Narrowing the list can leave the current page out of range.
      setPaginationParams((prev) => ({ ...prev, [GetUrlParams.Page]: '1' }));
    },
    [setPaginationParams],
  );

  return (
    <div>
      <Search
        onChange={onSearchChange}
        placeholder={t('search-by-user-name')}
        useSearchParam={false}
      />
      <Table
        columns={columns}
        data={rows}
        loading={loading}
        orderingState={orderingState}
        paginationState={paginationState}
        totalEntries={totalCount}
      />
    </div>
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

function AiUsageByUserModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      mode={ModalMode.FullScreen}
      onClose={onClose}
      title={t('ai-usage-by-user')}
    >
      {isOpen ? <AiUsageByUser /> : <div />}
    </Modal>
  );
}

export default memo(AiUsageByUserModal);
